import { useMemo } from 'react';
import { CHART_SERIES_PALETTE } from '../../theme/theme';

// ── Number formatter: comma thousands, dot decimal, 2 decimals ──
const fmtNum = (val) =>
  typeof val === 'number'
    ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : val;

// ── Shared glassmorphism tooltip config ──
const glassTooltip = {
  backgroundColor: 'rgba(255,255,255,0.88)',
  borderColor: 'rgba(0,0,0,0.08)',
  borderWidth: 1,
  padding: [8, 12],
  textStyle: { color: '#0F172A', fontSize: 13 },
  extraCssText:
    'backdrop-filter:blur(8px);border-radius:8px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.07);',
};

// ── Shared axis-name style ──
const axisNameStyle = {
  fontSize: 14,
  color: '#64748B',
  fontWeight: 700,
  padding: [0, 0, 0, 0],
  backgroundColor: 'rgba(255,153,0,0.08)',
  borderRadius: 4,
  padding: [4, 8],
};

// ── Builders ──

function buildLineOption(data, { color, xAxisLabel, yAxisLabel }) {
  const names = data.map((d) => d.name);
  const values = data.map((d) => d.value);
  const lineColor = color || CHART_SERIES_PALETTE[0];

  return {
    tooltip: {
      trigger: 'axis',
      ...glassTooltip,
      formatter: (params) =>
        params.map((p) => `${p.marker}${p.name}: <b>${fmtNum(p.value)}</b>`).join('<br>'),
    },
    xAxis: {
      type: 'category',
      data: names,
      axisLabel: { rotate: 45, fontSize: 12, hideOverlap: true },
      ...(xAxisLabel ? { name: xAxisLabel, nameLocation: 'center', nameGap: 48, nameTextStyle: axisNameStyle } : {}),
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
      axisLabel: { hideOverlap: true, formatter: (val) => fmtNum(val) },
      ...(yAxisLabel ? { name: yAxisLabel, nameLocation: 'middle', nameGap: 55, nameTextStyle: axisNameStyle } : {}),
    },
    series: [
      {
        type: 'line',
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        emphasis: { focus: 'series', itemStyle: { borderWidth: 2 } },
        lineStyle: { width: 2.5, color: lineColor },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: lineColor + '26' },
              { offset: 1, color: 'transparent' },
            ],
          },
        },
      },
    ],
    grid: { top: 24, right: 16, bottom: xAxisLabel ? 68 : 48, left: yAxisLabel ? 70 : 48, containLabel: true },
    dataZoom:
      data.length > 30
        ? [{ type: 'inside' }, { type: 'slider', height: 20 }]
        : [],
  };
}

function buildBarOption(data, { colors, barLayout, color, xAxisLabel, yAxisLabel }) {
  const isHorizontal = barLayout === 'horizontal';
  const seriesColors = colors || CHART_SERIES_PALETTE;
  const catLabel = isHorizontal ? yAxisLabel : xAxisLabel;
  const valLabel = isHorizontal ? xAxisLabel : yAxisLabel;

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      ...glassTooltip,
      formatter: (params) =>
        params.map((p) => `${p.marker}${p.name}: <b>${fmtNum(p.value)}</b>`).join('<br>'),
    },
    [isHorizontal ? 'yAxis' : 'xAxis']: {
      type: 'category',
      data: data.map((d) => d.name),
      axisLabel: { rotate: isHorizontal ? 0 : 45, hideOverlap: true },
      ...(catLabel ? { name: catLabel, nameLocation: isHorizontal ? 'middle' : 'center', nameGap: isHorizontal ? 110 : 48, nameTextStyle: axisNameStyle } : {}),
    },
    [isHorizontal ? 'xAxis' : 'yAxis']: {
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
      axisLabel: { hideOverlap: true, formatter: (val) => fmtNum(val) },
      ...(valLabel ? { name: valLabel, nameLocation: isHorizontal ? 'center' : 'middle', nameGap: isHorizontal ? 48 : 55, nameTextStyle: axisNameStyle } : {}),
    },
    series: [
      {
        type: 'bar',
        data: data.map((d, i) => ({
          value: d.value,
          itemStyle: {
            color: d.color || color || '#e68a00',
            borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
          },
        })),
        barWidth: '60%',
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' },
        },
      },
    ],
    grid: {
      top: 24,
      right: 16,
      bottom: (isHorizontal ? valLabel : catLabel) ? 68 : 48,
      left: isHorizontal ? 120 : (valLabel ? 70 : 48),
      containLabel: true,
    },
  };
}

function buildPieOption(data, { colors }) {
  const seriesColors = colors || CHART_SERIES_PALETTE;
  const total = data.reduce((s, d) => s + (d.value || 0), 0);

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      ...glassTooltip,
    },
    legend: {
      bottom: 0,
      type: 'scroll',
      textStyle: { fontSize: 12, color: '#64748B' },
    },
    series: [
      {
        type: 'pie',
        radius: ['50%', '75%'],
        center: ['50%', '45%'],
        data: data.map((d, i) => ({
          name: d.name,
          value: Number(d.value) || 0,
          itemStyle: {
            color: d.color || seriesColors[i % seriesColors.length],
          },
        })),
        label: {
          show: true,
          position: 'outside',
          formatter: '{c}',
          fontSize: 11,
          color: '#334155',
        },
        labelLine: {
          show: true,
          length: 10,
          length2: 8,
          lineStyle: { color: '#94A3B8' },
        },
        emphasis: {
          label: { show: true, fontSize: 14, fontWeight: 600 },
          scaleSize: 6,
        },
        itemStyle: {
          borderRadius: 6,
          borderColor: '#fff',
          borderWidth: 2,
        },
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '42%',
        style: {
          text: fmtNum(total),
          fontSize: 22,
          fontWeight: 700,
          fill: '#0F172A',
          textAlign: 'center',
        },
      },
    ],
  };
}

function buildHistogramOption(data, { colors, color, xAxisLabel, yAxisLabel }) {
  const seriesColors = colors || CHART_SERIES_PALETTE;

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      ...glassTooltip,
      formatter: (params) =>
        params.map((p) => `${p.marker}${p.name}: <b>${fmtNum(p.value)}</b>`).join('<br>'),
    },
    xAxis: {
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
      axisLabel: { hideOverlap: true, formatter: (val) => fmtNum(val) },
      ...(xAxisLabel ? { name: xAxisLabel, nameLocation: 'center', nameGap: 40, nameTextStyle: axisNameStyle } : {}),
    },
    yAxis: {
      type: 'category',
      data: data.map((d) => d.name),
      axisLabel: { fontSize: 12, hideOverlap: true },
      ...(yAxisLabel ? { name: yAxisLabel, nameLocation: 'middle', nameGap: 110, nameTextStyle: axisNameStyle } : {}),
    },
    series: [
      {
        type: 'bar',
        data: data.map((d, i) => ({
          value: d.value,
          itemStyle: {
            color: d.color || (color ? color : seriesColors[i % seriesColors.length]),
            borderRadius: [0, 4, 4, 0],
          },
        })),
        barWidth: '60%',
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' },
        },
      },
    ],
    grid: { top: 24, right: 16, bottom: xAxisLabel ? 52 : 24, left: 120, containLabel: true },
  };
}

// ── Hook ──

const builders = {
  line: buildLineOption,
  bar: buildBarOption,
  pie: buildPieOption,
  histogram: buildHistogramOption,
};

export default function useChartOption(type, data, opts = {}) {
  return useMemo(() => {
    if (!data || data.length === 0) return null;
    if (type === 'pie' && data.every((d) => !d.value || Number(d.value) <= 0)) return null;
    const build = builders[type] || builders.bar;
    return build(data, opts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, data, opts.color, opts.colors, opts.barLayout, opts.xAxisLabel, opts.yAxisLabel]);
}
